import { memo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';

import { excludeKey, setErrors } from '@utils/object';
import { FormProvider, useForm } from '@hooks/react-hook-form';
import { Button, useSnackbar } from '@new-components/elements';
import {
  useRGetApplicationData,
  FormParam,
  useRSaveApplication,
  useRGetSavedApplication,
  SavedApplicationDataResponse,
  useRVerifyBankAccount,
} from '@repositories/new-application';
import DialogBankAccountValidation from './components/dialogs/bank-account-validation';
import DialogFailedBankAccountValidation from './components/dialogs/failed-bank-account-verification';

import ApplicationCard from '../../application-card';

import {
  SectionAccountInformation,
  SectionExpenseIncome,
  SectionPreLoan,
} from './components/sections';
import { FinancialProps } from './types';
import { defaultValues, financialSchema } from './constants';

const Financial = (props: FinancialProps) => {
  const { setStep } = props;
  const { onOpen } = useSnackbar();
  const [showBankValidationModal, setShowBankValidationModal] = useState(false);
  const [showFailedBankValidationModal, setShowFailedBankValidationModal] =
    useState(false);

  const form = useForm<FormParam<'financial'>>({
    defaultValues,
    shouldFocusError: false,
    resolver: yupResolver(financialSchema),
    mode: 'onChange',
  });
  const { handleSubmit, setError, reset, getValues, setValue } = form;

  const { data } = useRGetSavedApplication<
    SavedApplicationDataResponse<'financial'>
  >({
    step: 3,
    onSuccess: ({ data }) => {
      reset({ ...getValues(), ...data });
    },
  });

  const { isLoading: isLoadingGetApplicationData } = useRGetApplicationData({
    page: 3,
  });

  const { mutate: mutateSaveForm, isLoading: isSavingForm } =
    useRSaveApplication({
      onError: (err) => {
        const statusCode = err.payload?.statusCode;
        const errors = (err.payload?.errors || {}) as FormParam<'financial'>;

        switch (statusCode) {
          case 400:
            setErrors<FormParam<'financial'>>(errors, setError);
            break;

          default:
            onOpen({
              message:
                (err.payload?.errors as Array<string>)?.[0] ||
                'Opps, Something went wrong.',
              severity: 'error',
            });
            break;
        }
      },
    });

  const { mutate: verifyBankAccount, isLoading: isLoadingVerifyBankAccount } =
    useRVerifyBankAccount({
      onSuccess: () => {
        setShowBankValidationModal(false);
        const fields = getValues();
        mutateSaveForm(
          {
            variables: {
              ...excludeKey<SavedApplicationDataResponse<'financial'>>(
                fields as SavedApplicationDataResponse<'financial'>,
                ['currentStep'],
              ),
              step: 3,
            },
          },
          {
            onSuccess: () => {
              setValue('bankAccountNumberVerified', true);
              setShowBankValidationModal(false);
              setStep((prev) => prev + 1);
            },
          },
        );
      },
      onError: (err) => {
        const payload = (err.payload || {
          statusCode: 400,
          errors: [],
        }) as {
          statusCode: number;
          errors: string[];
        };
        const statusCode = payload.statusCode;
        setValue('bankAccountNumberVerified', false);
        switch (statusCode) {
          case 400:
            setShowBankValidationModal(false);
            setShowFailedBankValidationModal(true);
            setTimeout(() => {
              setError('bankAccountNumber', {
                message: payload.errors[0],
              });
            }, 100);
            break;

          default:
            break;
        }
      },
    });

  const handleVerify = () => {
    verifyBankAccount({
      variables: {
        bankAccountNumber: getValues('bankAccountNumber') || '',
        bankName: getValues('bankName') || '',
      },
    });
  };

  async function onSubmit() {
    setShowBankValidationModal(true);
  }

  return (
    <FormProvider scrollToFieldOnError {...form}>
      <Helmet>
        <title>JULO - Form Informasi Keuangan</title>
      </Helmet>
      <form
        id='financial-form'
        onSubmit={handleSubmit(onSubmit)}
        className='family-information'
      >
        <SectionExpenseIncome />
        <SectionAccountInformation
          defaultIsVerifiedAccount={(data?.data.currentStep ?? 1) > 3}
        />

        <SectionPreLoan />

        <ApplicationCard>
          <Button
            type='submit'
            disabled={isSavingForm || isLoadingGetApplicationData}
            fullWidth
          >
            Lanjutkan
          </Button>
        </ApplicationCard>

        <DialogBankAccountValidation
          isOpen={showBankValidationModal}
          onCancel={() => {
            setValue('bankAccountNumberVerified', false);
            setShowBankValidationModal(false);
          }}
          onVerify={handleVerify}
          isLoading={isLoadingVerifyBankAccount}
        />

        <DialogFailedBankAccountValidation
          isOpen={showFailedBankValidationModal}
          onClose={() => setShowFailedBankValidationModal(false)}
        />
      </form>
    </FormProvider>
  );
};

export default memo(Financial);
